import { useMemo } from 'react';
import { useAppStore } from '../store';

export interface KeyMetrics {
  totalSpend: number;
  dailyAverage: number;
  projectedMonthEnd: number | null;
  largestCategory: { name: string; percentage: number } | null;
  comparison: {
    totalSpendChange: number | null;
    dailyAverageChange: number | null;
    projectedMonthEndChange: number | null;
  };
  daysElapsed: number;
  daysInMonth: number;
}

function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getMonthRange(
  year: number,
  month: number
): { start: string; end: string } {
  const daysInMonth = getDaysInMonth(year, month);
  return {
    start: formatDate(new Date(year, month, 1)),
    end: formatDate(new Date(year, month, daysInMonth)),
  };
}

function getPreviousMonth(
  year: number,
  month: number
): { year: number; month: number } {
  if (month === 0) {
    return { year: year - 1, month: 11 };
  }
  return { year, month: month - 1 };
}

export function useKeyMetrics(): KeyMetrics {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);

  return useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    const currentMonthRange = getMonthRange(currentYear, currentMonth);
    const { year: prevYear, month: prevMonth } = getPreviousMonth(
      currentYear,
      currentMonth
    );
    const previousMonthRange = getMonthRange(prevYear, prevMonth);

    const filterTransactions = (startDate: string, endDate: string) => {
      return transactions.filter((t) => {
        if (t.type !== 'expense') return false;
        if (t.excludedFromBudget) return false;
        if (t.date < startDate || t.date > endDate) return false;
        return true;
      });
    };

    const currentTransactions = filterTransactions(
      currentMonthRange.start,
      currentMonthRange.end
    );

    const latestTransactionDay = currentTransactions.reduce((maxDay, t) => {
      const day = parseInt(t.date.slice(8, 10), 10);
      return day > maxDay ? day : maxDay;
    }, 0);

    const comparisonDay =
      latestTransactionDay > 0 ? latestTransactionDay : currentDay;

    const prevMonthDays = getDaysInMonth(prevYear, prevMonth);
    const adjustedComparisonDay = Math.min(comparisonDay, prevMonthDays);
    const previousMonthComparisonEnd = formatDate(
      new Date(prevYear, prevMonth, adjustedComparisonDay)
    );

    const previousTransactions = filterTransactions(
      previousMonthRange.start,
      previousMonthRange.end
    );

    const previousTransactionsForComparison = filterTransactions(
      previousMonthRange.start,
      previousMonthComparisonEnd
    );

    const totalSpend = currentTransactions.reduce(
      (sum, t) => sum + Math.abs(t.amount),
      0
    );

    const previousTotalSpendForComparison =
      previousTransactionsForComparison.reduce(
        (sum, t) => sum + Math.abs(t.amount),
        0
      );

    const previousTotalSpend = previousTransactions.reduce(
      (sum, t) => sum + Math.abs(t.amount),
      0
    );

    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const daysElapsed = comparisonDay > 0 ? comparisonDay : currentDay;
    const dailyAverage = daysElapsed > 0 ? totalSpend / daysElapsed : 0;

    const previousDaysInMonth = getDaysInMonth(prevYear, prevMonth);
    const previousDailyAverage =
      previousDaysInMonth > 0 ? previousTotalSpend / previousDaysInMonth : 0;

    const projectedMonthEnd = dailyAverage * daysInMonth;

    const categorySpending = new Map<string, number>();
    currentTransactions.forEach((t) => {
      const categoryId = t.categoryId ?? 'uncategorized';
      const current = categorySpending.get(categoryId) ?? 0;
      categorySpending.set(categoryId, current + Math.abs(t.amount));
    });

    let largestCategory: { name: string; percentage: number } | null = null;
    if (totalSpend > 0 && categorySpending.size > 0) {
      let maxCategoryId = '';
      let maxAmount = 0;
      categorySpending.forEach((amount, categoryId) => {
        if (amount > maxAmount) {
          maxAmount = amount;
          maxCategoryId = categoryId;
        }
      });

      const category = categories.find((c) => c.id === maxCategoryId);
      const categoryName =
        maxCategoryId === 'uncategorized'
          ? 'Sin categoría'
          : (category?.name ?? 'Desconocida');
      const percentage = (maxAmount / totalSpend) * 100;
      largestCategory = { name: categoryName, percentage };
    }

    let totalSpendChange: number | null = null;
    let dailyAverageChange: number | null = null;
    let projectedMonthEndChange: number | null = null;

    if (previousTotalSpendForComparison > 0) {
      totalSpendChange =
        ((totalSpend - previousTotalSpendForComparison) /
          previousTotalSpendForComparison) *
        100;
    }
    if (previousDailyAverage > 0) {
      dailyAverageChange =
        ((dailyAverage - previousDailyAverage) / previousDailyAverage) * 100;
    }
    if (previousTotalSpend > 0 && projectedMonthEnd !== null) {
      projectedMonthEndChange =
        ((projectedMonthEnd - previousTotalSpend) / previousTotalSpend) * 100;
    }

    return {
      totalSpend,
      dailyAverage,
      projectedMonthEnd,
      largestCategory,
      comparison: {
        totalSpendChange,
        dailyAverageChange,
        projectedMonthEndChange,
      },
      daysElapsed,
      daysInMonth,
    };
  }, [transactions, categories]);
}
